import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Input, Select, Switch } from '@dreamtree-org/twreact-ui';
import { Search } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import { filterRows, searchableKeys } from '@/components/dataTableFilter';
import { applyModelFilter, isShown, MODEL_FILTERS } from '@/lib/modelFilters';

/**
 * One catalogue scope — a credential and the models it can run — with its Sync button, its
 * filters, and an editable paginated table.
 *
 * Rendered by BOTH the AI Models page (which picks which scope) and the panel on a
 * connection's own detail page (which only ever has one). Extracted rather than duplicated
 * because the table encodes rules the two screens must not disagree about: which edits mark
 * a row `manual`, and that a rejected price reverts instead of appearing saved.
 *
 * Search and filter state live HERE rather than inside <DataTable>: the bulk action must
 * write to exactly the rows the user is looking at, and DataTable's internal search would
 * hide that set from us. The filtering itself still uses DataTable's own helpers, so there
 * is one implementation, not two.
 */
function apiError(ex) {
  return (ex.response && ex.response.data && ex.response.data.error) || ex.message;
}

/** Blur-to-save price cell — a controlled input that only commits on blur, so typing
 *  "12.5" does not fire a request per keystroke. */
function PriceCell({ value, onSave, disabled }) {
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  useEffect(() => { setDraft(value == null ? '' : String(value)); }, [value]);
  return (
    <Can module="connections" action="author" fallback={<span className="text-xs">{value == null ? '—' : value}</span>}>
      <input
        className="border rounded px-1 py-0.5 text-xs w-20 bg-[var(--app-surface)] disabled:opacity-50"
        placeholder="—"
        disabled={disabled}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const next = draft.trim();
          if (next === (value == null ? '' : String(value))) return;
          onSave(next === '' ? null : next);
        }}
      />
    </Can>
  );
}

/** "Never synced" reads very differently from "synced and empty" — one is a button you
 *  have not pressed, the other is a provider that returned nothing. */
function syncedLabel(scope) {
  if (!scope.synced_at) return 'never synced';
  return `synced ${new Date(scope.synced_at).toLocaleString()}${scope.stale ? ' — stale' : ''}`;
}

function ScopeHeader({ scope, count, syncing, onSync }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{scope.name}</div>
        <div className="text-xs text-secondary-500">
          <span className="font-mono">{scope.provider}</span>
          {' · '}
          {scope.kind === 'env' ? 'shared app key (.env)' : 'connection'}
          {' · '}
          {count} model(s)
          {' · '}
          {syncedLabel(scope)}
        </div>
      </div>
      <Can module="connections" action="rotate" fallback={null}>
        <Button size="sm" variant="secondary" onClick={onSync} disabled={syncing || !scope.supported}>
          {syncing ? 'Syncing…' : 'Sync models'}
        </Button>
      </Can>
    </div>
  );
}

/** Flips every currently-visible row at once. Labelled with the live count because the
 *  set it writes to is whatever the filter and search happen to select right now. */
function BulkVisibility({ rows, busy, onApply }) {
  const hideable = rows.filter(isShown);
  const showable = rows.filter((m) => !isShown(m));
  const act = (targets, enabled) => {
    const verb = enabled ? 'Show' : 'Hide';
    if (!window.confirm(`${verb} ${targets.length} model(s)?`)) return;
    onApply(targets.map((m) => m.id), enabled);
  };
  return (
    <Can module="connections" action="author" fallback={null}>
      <div className="flex gap-2">
        {hideable.length > 0 && (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => act(hideable, false)}>
            Hide these {hideable.length}
          </Button>
        )}
        {showable.length > 0 && (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => act(showable, true)}>
            Show these {showable.length}
          </Button>
        )}
      </div>
    </Can>
  );
}

function buildColumns({ busyId, patch }) {
  return [
    {
      key: 'model_id',
      label: 'Model',
      render: (m) => (
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs">{m.model_id}</span>
          {m.source === 'manual' && (
            <Badge variant="secondary" size="sm" title="Edited by hand — a sync will not overwrite its price">manual</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'label',
      label: 'Name',
      render: (m) => <span className="text-xs text-secondary-500">{m.label && m.label !== m.model_id ? m.label : '—'}</span>,
    },
    {
      key: 'context_window',
      label: 'Context',
      searchable: false,
      render: (m) => <span className="text-xs text-secondary-500">{m.context_window ? Number(m.context_window).toLocaleString() : '—'}</span>,
    },
    {
      key: 'input_price',
      label: '$/1M in',
      searchable: false,
      render: (m) => <PriceCell value={m.input_price} disabled={busyId === m.id} onSave={(v) => patch(m.id, { input_price: v })} />,
    },
    {
      key: 'output_price',
      label: '$/1M out',
      searchable: false,
      render: (m) => <PriceCell value={m.output_price} disabled={busyId === m.id} onSave={(v) => patch(m.id, { output_price: v })} />,
    },
    {
      key: 'enabled',
      label: 'Shown',
      searchable: false,
      render: (m) => (
        <Switch
          checked={isShown(m)}
          disabled={busyId === m.id}
          onChange={(checked) => patch(m.id, { enabled: checked })}
        />
      ),
    },
  ];
}

export default function ModelCatalogScope({ scope, onChanged }) {
  const { fetchApi } = useAppApi();
  const [models, setModels] = useState(scope.models || []);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  // Rows follow whatever the parent last loaded.
  useEffect(() => { setModels(scope.models || []); }, [scope]);

  // Switching credential resets the view: carrying a search from one provider's catalogue
  // into another's shows an empty table for no visible reason, and a stale "12 hidden"
  // notice would be about models that are no longer on screen.
  useEffect(() => {
    setFilter('all');
    setQuery('');
    setMsg('');
    setError('');
  }, [scope.key]);

  const sync = async () => {
    setSyncing(true);
    setMsg('');
    setError('');
    try {
      const res = await fetchApi('modelCatalogSync', {
        provider: scope.provider,
        connection_id: scope.connection_id || undefined,
      });
      setMsg(`${res.total} model(s) — ${res.added} new, ${res.removed} removed.`);
      if (onChanged) await onChanged();
    } catch (ex) {
      setError(apiError(ex));
    } finally {
      setSyncing(false);
    }
  };

  // Optimistic on the row, authoritative from the response: a rejected price (negative,
  // non-numeric) must not linger on screen as if it saved.
  const patch = async (rowId, data) => {
    setBusyId(rowId);
    try {
      const fresh = await fetchApi('modelCatalogUpdate', { rowId, ...data });
      setModels((list) => list.map((m) => (m.id === rowId ? fresh : m)));
      setError('');
    } catch (ex) {
      setError(apiError(ex));
      if (onChanged) await onChanged();
    } finally {
      setBusyId(null);
    }
  };

  const bulk = async (ids, enabled) => {
    setBulkBusy(true);
    setMsg('');
    setError('');
    try {
      const res = await fetchApi('modelCatalogBulk', { model_ids: ids, enabled });
      setMsg(`${res.updated} model(s) ${enabled ? 'shown' : 'hidden'}.`);
      if (onChanged) await onChanged();
    } catch (ex) {
      setError(apiError(ex));
    } finally {
      setBulkBusy(false);
    }
  };

  const columns = useMemo(() => buildColumns({ busyId, patch }), [busyId]);
  const rows = useMemo(
    () => filterRows(applyModelFilter(models, filter), query, searchableKeys(columns)),
    [models, filter, query, columns],
  );

  return (
    <div className="space-y-3">
      <Card className="p-3 space-y-2">
        <ScopeHeader scope={scope} count={models.length} syncing={syncing} onSync={sync} />
        {!scope.supported && <p className="text-xs text-secondary-500">This provider has no model-discovery endpoint — agents fall back to a built-in list, or a model name typed by hand.</p>}
        {error && <Alert variant="error" title="Model catalogue error">{error}</Alert>}
        {msg && <p className="text-sm text-emerald-600">{msg}</p>}
      </Card>

      {!models.length ? (
        <Card className="p-6 text-center text-sm text-secondary-500">
          {scope.supported
            ? 'Not synced yet — Sync models asks the provider which models this key can use.'
            : 'Nothing to show for this provider.'}
        </Card>
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          search={false}
          pageSize={25}
          emptyText="No model matches this filter."
          onReload={onChanged}
          toolbar={(
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-44">
                <Select options={MODEL_FILTERS} value={filter} onChange={setFilter} />
              </div>
              <div className="w-56">
                <Input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search models…"
                  icon={<Search className="h-4 w-4" />}
                  iconPosition="left"
                  clearable
                  onClear={() => setQuery('')}
                  aria-label="Search models"
                />
              </div>
              <BulkVisibility rows={rows} busy={bulkBusy} onApply={bulk} />
            </div>
          )}
        />
      )}
    </div>
  );
}
